import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';

export default function AfbudBetaling() {
  const { lang } = useLanguage();

  // SEO Metadata
  const seoTitle = lang === 'da' 
    ? 'Afbuds- og betalingsbetingelser | Nordisk Psykiatri' 
    : 'Avboknings- och betalningsvillkor | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Læs vores betingelser for afbud, udeblivelse og betaling hos Nordisk Psykiatri.'
    : 'Läs våra villkor för avbokning, utebliven ankomst och betalning hos Nordisk Psykiatri.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const [cmsData, setCmsData] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;
    fetch('/_api/public/content/informationssider')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch CMS content');
        return res.json();
      })
      .then((data) => {
        if (!isMounted) return;
        const items = Array.isArray(data) ? data : data?.items || [];
        const entry = items.find((item) => item.side_slug === 'afbud-og-betaling');
        if (entry) {
          setCmsData(entry);
        }
      })
      .catch((err) => {
        console.warn('Could not load afbud-og-betaling from CMS, using fallback', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const content = {
    da: {
      eyebrow: 'Praktisk information',
      title: 'Afbuds- og betalingsbetingelser',
      intro: 'Vi ønsker at sikre en fair og effektiv planlægning, så vi kan tilbyde tid til så mange som muligt.',
      sections: [
        {
          title: 'Afbud',
          paragraphs: [
            'Afbud eller ombooking skal ske senest 48 timer før den aftalte tid. Dette kan gøres via Planway eller ved at kontakte klinikken.',
            'Ved afbud senere end 48 timer før, udeblivelse eller afbud på helligdage og i weekender, opkræves fuldt honorar for den aftalte tid.',
            'Gentagne udeblivelser kan medføre, at fremtidige tider ikke kan bookes.'
          ]
        },
        {
          title: 'Betaling',
          paragraphs: [
            'Betaling sker via faktura, som fremsendes efter hver konsultation.',
            'Ved forløbsaftaler, hvor andet er aftalt, fremsendes faktura med 8 dages betalingsfrist.',
            'Udeblivelsesgebyrer faktureres efterfølgende.'
          ]
        }
      ]
    },
    sv: {
      eyebrow: 'Praktisk information',
      title: 'Avboknings- och betalningsvillkor',
      intro: 'Vi vill säkerställa en rättvis och effektiv planering så att vi kan erbjuda tider till så många som möjligt.',
      sections: [
        {
          title: 'Avbokning',
          paragraphs: [
            'Avbokning eller ombokning måste ske senast 48 timmar före avtalad tid. Detta kan göras via Planway eller genom att kontakta kliniken.',
            'Vid avbokning senare än 48 timmar före, utebliven ankomst eller avbokning på helgdagar och helger debiteras hela arvodet för den avtalade tiden.',
            'Upprepade uteblivna ankomster kan leda till att framtida tider inte kan bokas.'
          ]
        },
        {
          title: 'Betalning',
          paragraphs: [
            'Betalning sker via faktura som skickas efter varje konsultation.',
            'Vid förloppsavtal där annat har avtalats skickas faktura med 8 dagars betalningstid.',
            'Uteblivandeavgifter faktureras i efterhand.'
          ]
        }
      ]
    }
  };

  const t = content[lang] || content.da;

  // Extract localized CMS fields if available
  const getLoc = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[lang] || val.da || val.en || Object.values(val)[0] || '';
  };

  const pageEyebrow = cmsData?.undertitel_eller_badge ? getLoc(cmsData.undertitel_eller_badge) : (t.eyebrow || 'Praktisk information');
  const pageTitle = cmsData?.titel ? getLoc(cmsData.titel) : t.title;
  const pageHtml = cmsData?.indhold ? getLoc(cmsData.indhold) : null;
  const lastUpdated = cmsData?.sidst_opdateret ? getLoc(cmsData.sidst_opdateret) : null;

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      {/* HERO */}
      <FluidSubpageHero
        className="bg-[#f6f0e2]"
        style={{ backgroundColor: '#f6f0e2' }}
        eyebrow={pageEyebrow}
        title={pageTitle}
        description={pageHtml ? undefined : t.intro}
      />

      {/* Clean, Simple Terms Content */}
      <section className="py-12 md:py-16 bg-sand-light">
        <div className="mx-auto max-w-4xl px-6">
          {pageHtml ? (
            <div className="prose-article max-w-none text-[#2d3237] leading-relaxed font-light [&>h2]:font-display [&>h2]:text-2xl [&>h2]:font-light [&>h2]:text-petrol-dark [&>h2]:mb-4 [&>h2]:mt-8 [&>h2:first-child]:mt-0 [&>p]:mb-4 [&>hr]:my-10 [&>hr]:border-[#ece8e1]">
              <div dangerouslySetInnerHTML={{ __html: pageHtml }} />
              {lastUpdated && (
                <div className="mt-12 pt-6 border-t border-[#ece8e1] text-xs text-[#566869]">
                  {lastUpdated}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-12">
              {t.sections.map((section, idx) => (
                <React.Fragment key={idx}>
                  <div className="text-left">
                    <h2 className="font-display text-2xl font-light text-petrol-dark mb-4">
                      {section.title}
                    </h2>
                    <div className="space-y-4 text-base text-[#2d3237] leading-relaxed font-light">
                      {section.paragraphs.map((p, pIdx) => (
                        <p key={pIdx}>
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                  {idx < t.sections.length - 1 && (
                    <hr className="my-10 border-[#ece8e1]" />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
